import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Eye, ChevronLeft, Tag, User } from "lucide-react";
import DOMPurify from "dompurify";
import api from "../lib/api";
import Seo from "../components/Seo";
import ShareButtons from "../components/ShareButtons";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPost(null); setError(false);
    api.get(`/blog/posts/${slug}`)
      .then(({ data }) => setPost(data))
      .catch(() => setError(true));
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (error) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center">
      <h2 className="font-display font-bold text-2xl text-cm-primary">Article not found</h2>
      <Link to="/blog" className="mt-4 inline-block text-cm-accent font-medium">← Back to blog</Link>
    </div>
  );
  if (!post) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-slate-500">Loading…</div>;

  return (
    <div data-testid="blog-post-page" className="bg-white min-h-screen">
      <Seo title={post.title} description={post.excerpt} image={post.cover_image} type="article" />

      <article>
        {post.cover_image && (
          <div className="w-full aspect-[21/9] sm:aspect-[16/6] overflow-hidden bg-slate-100">
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-14">
          <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-1 text-sm text-cm-accent font-medium hover:underline">
            <ChevronLeft className="w-4 h-4" /> Back to blog
          </Link>

          <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 text-cm-accent text-xs font-semibold uppercase tracking-[0.15em]">
            <Tag className="w-3 h-3" /> {post.category}
          </div>

          <h1 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-cm-primary leading-[1.1]">{post.title}</h1>

          {post.excerpt && <p className="mt-4 text-lg text-slate-600 leading-relaxed">{post.excerpt}</p>}

          <div className="mt-6 flex items-center justify-between flex-wrap gap-4 pb-6 border-b border-slate-200">
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {post.author_name || "Car Market"}</span>
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {new Date(post.published_at || post.created_at).toLocaleDateString()}</span>
              <span className="inline-flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> {post.views || 0} views</span>
            </div>
            <ShareButtons compact title={post.title} description={post.excerpt} />
          </div>

          <div data-testid="blog-body"
               className="blog-body prose prose-slate max-w-none mt-8 prose-headings:font-display prose-headings:text-cm-primary prose-headings:font-extrabold prose-h2:text-2xl prose-h2:mt-8 prose-h3:text-xl prose-p:text-slate-700 prose-p:leading-relaxed prose-a:text-cm-accent prose-a:no-underline hover:prose-a:underline prose-strong:text-cm-primary prose-blockquote:border-l-cm-accent prose-blockquote:text-slate-600"
               dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.body, { ADD_TAGS: ["iframe"], ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling"] }) }} />

          {post.tags?.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2">
              {post.tags.map((t) => (
                <span key={t} className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">#{t}</span>
              ))}
            </div>
          )}

          <div className="mt-10">
            <ShareButtons title={post.title} description={post.excerpt} />
          </div>

          {post.related?.length > 0 && (
            <div className="mt-14">
              <h3 className="font-display font-extrabold text-2xl text-cm-primary mb-4">Related articles</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {post.related.map((r) => (
                  <Link key={r.id} to={`/blog/${r.slug}`} className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-all">
                    {r.cover_image && (
                      <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
                        <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      </div>
                    )}
                    <div className="p-4">
                      <h4 className="font-display font-bold text-cm-primary line-clamp-2">{r.title}</h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </div>
  );
}
